import { useState } from "react";
import { AlertTriangle, CheckCircle2, MapPin, Phone, Truck } from "lucide-react";
import { useI18n } from "../../../i18n";
import { todayAthens } from "../../../lib/format";
import { useApi } from "../../customer/hooks";
import { PageHead, btnGhost, btnGreen } from "../../../components/common/Page";
import { DateField } from "../../../components/common/DateField";
import { StatusBadge } from "../../../components/common/StatusBadge";
import { EmptyState, SkeletonBlock } from "../../../components/common/EmptyState";
import { IssueDialog, ProofDialog } from "./Dialogs";

const Stop = ({ s, onPod, onIssue }) => {
  const { t, lang } = useI18n();
  const done = s.status === "delivered" || s.status === "invoiced";
  const a = `${btnGhost} flex-1 px-3`;
  return (
    <li className={`rounded-2xl bg-white p-4 shadow-sm ${done ? "opacity-70" : ""}`} data-testid={`stop-${s.local_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0"><p className="font-semibold text-navy">{s.customer_name}</p><p className="text-sm text-slate-500">{s.address}</p><p className="text-xs text-slate-400">{s.doc_no} · {t("x.route.items", { n: s.items.length })}</p></div>
        <StatusBadge status={s.status} />
      </div>
      <details className="mt-2 text-sm"><summary className="cursor-pointer text-xs font-medium text-slate-500">{t("x.route.items", { n: s.items.length })}</summary>
        <ul className="mt-1 space-y-0.5 text-navy">{s.items.map((i) => <li key={`${i.item_code}:${i.uom}`} className="flex justify-between gap-2"><span className="truncate">{i[`name_${lang}`]}</span><b className="tabular-nums">{i.qty_boxes} {t(i.uom === "piece" ? "x.uom.piece" : "x.uom.box")}</b></li>)}</ul>
      </details>
      {s.pod && <p className="mt-2 flex items-center gap-1 text-xs font-medium text-brand" data-testid={`stop-pod-${s.local_id}`}><CheckCircle2 className="h-4 w-4" />{t("x.route.receivedBy", { name: s.pod.receiver_name })}</p>}
      {s.issues > 0 && <p className="mt-1 flex items-center gap-1 text-xs font-medium text-rose-600"><AlertTriangle className="h-4 w-4" />{t(s.issues === 1 ? "x.route.issue1" : "x.route.issues", { n: s.issues })}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <a href={`tel:${s.phone.replace(/\s/g, "")}`} className={a} data-testid={`stop-call-${s.local_id}`}><Phone className="h-4 w-4" />{t("x.route.call")}</a>
        <a href={s.map_url} target="_blank" rel="noreferrer" className={a} data-testid={`stop-map-${s.local_id}`}><MapPin className="h-4 w-4" />{t("x.route.map")}</a>
        <button type="button" onClick={() => onIssue(s)} className={a} data-testid={`stop-issue-${s.local_id}`}><AlertTriangle className="h-4 w-4 text-rose-600" />{t("x.route.issue")}</button>
        {!done && <button type="button" onClick={() => onPod(s)} className={`${btnGreen} w-full sm:w-auto`} data-testid={`stop-pod-btn-${s.local_id}`}><CheckCircle2 className="h-4 w-4" />{t("x.route.deliver")}</button>}
      </div>
    </li>
  );
};

export default function DriverRoute() {
  const { t } = useI18n();
  const [day, setDay] = useState(todayAthens());
  const [pod, setPod] = useState(null);
  const [issue, setIssue] = useState(null);
  const r = useApi("route", "/staff/deliveries/route", { day }, { staleTime: 0, refetchInterval: 60000 });
  const done = () => { setPod(null); setIssue(null); r.refetch(); };
  return (
    <div className="space-y-5" data-testid="route-page">
      <PageHead title={t("x.route.title")} sub={t("x.route.sub")}><div className="w-44"><DateField value={day} onChange={(v) => v && setDay(v)} testId="route-date" /></div></PageHead>
      {r.isLoading ? <SkeletonBlock className="h-64" /> : !r.data?.areas.length ? <EmptyState icon={Truck} title={t("x.route.empty")} testId="route-empty" /> : r.data.areas.map((g) => (
        <section key={g.area_code} className="space-y-3" data-testid={`route-area-${g.area_code}`}>
          <h2 className="flex items-baseline gap-2 text-lg font-semibold text-navy">{g.area_name}<span className="text-xs font-normal text-slate-500">{t("x.route.stops", { n: g.stops.length })}</span></h2>
          <ul className="grid gap-3 md:grid-cols-2">{g.stops.map((s) => <Stop key={s.local_id} s={s} onPod={setPod} onIssue={setIssue} />)}</ul>
        </section>
      ))}
      {pod && <ProofDialog stop={pod} onClose={() => setPod(null)} onDone={done} />}
      {issue && <IssueDialog stop={issue} onClose={() => setIssue(null)} onDone={done} />}
    </div>
  );
}
